import { useEffect, useRef, useState } from "react";
import { CircleDollarSign, Plus, Search, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { Pagination, usePagination } from "@/components/Pagination";
import { ContactPicker } from "@/components/ContactPicker";
import { SearchSelect } from "@/components/SearchSelect";

export const MODES = {
  RECEIVE: { title: "Penerimaan Uang", action: "Terima Pembayaran", contactType: "Customer", contactLabel: "Pelanggan", docLabel: "invoice", accountLabel: "Setor ke" },
  PAY: { title: "Pengeluaran Uang", action: "Kirim Pembayaran", contactType: "Supplier", contactLabel: "Supplier", docLabel: "tagihan", accountLabel: "Bayar dari" },
};
const METHODS = ["Tunai", "Transfer Bank", "Cek", "Giro", "Kartu Kredit"];
const today = () => new Date().toISOString().slice(0, 10);

export default function Payments({ mode }) {
  const cfg = MODES[mode];
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState(null);
  const load = () => api(`/payments?type=${mode}`).then(setRows).catch((e) => setMessage({ text: e.message, type: "error" }));
  useEffect(() => { setSearch(""); setSelected(null); setFormOpen(false); setEditing(null); setDeleting(null); setMessage(null); load(); }, [mode]); // eslint-disable-line
  const filtered = rows.filter((r) => [r.number, r.date, r.contact, r.account_name, r.method, r.reference].join(" ").toLowerCase().includes(search.toLowerCase()));
  const pager = usePagination(filtered, 25, `${mode}|${search}`);
  const onSaved = (record) => { setRows((prev) => [record, ...prev]); setFormOpen(false); setMessage({ text: `${record.number} berhasil dicatat dan jurnal kas otomatis diposting`, type: "success" }); };
  const onEdited = (record) => { setRows((prev) => prev.map((x) => (x.id === record.id ? record : x))); setSelected((prev) => (prev?.id === record.id ? record : prev)); setEditing(null); setMessage({ text: `${record.number} berhasil diubah`, type: "success" }); };
  const remove = async () => {
    try {
      await api(`/payments/${deleting.id}`, { method: "DELETE" });
      setRows((prev) => prev.filter((x) => x.id !== deleting.id));
      setSelected((prev) => (prev?.id === deleting.id ? null : prev));
      setMessage({ text: `${deleting.number} dihapus — jurnal kas dibatalkan dan status ${cfg.docLabel} dikembalikan`, type: "success" });
    } catch (e) { setMessage({ text: e.message, type: "error" }); }
    setDeleting(null);
  };
  return (
    <div className="workflow-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">KAS &amp; BANK</p>
          <h1>{cfg.title}</h1>
          <p className="muted">Catat pembayaran, lunasi {cfg.docLabel}, dan biarkan jurnal kas dibuat otomatis.</p>
        </div>
        <button data-testid="payment-create-button" className="primary-btn" onClick={() => { setFormOpen(true); setSelected(null); }}><Plus size={16} /> {cfg.action}</button>
      </div>
      {message && <div data-testid={message.type === "error" ? "payment-error-message" : "payment-success-message"} className={`success-message ${message.type === "error" ? "error" : ""}`}>{message.text}<button data-testid="payment-dismiss-message" onClick={() => setMessage(null)}><X size={14} /></button></div>}
      <div className="workflow-toolbar surface">
        <div className="workflow-search"><Search size={16} /><input data-testid="payment-search-input" placeholder={`Cari ${cfg.title.toLowerCase()}...`} value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <span className="record-count">{filtered.length} data</span>
      </div>
      <div className="surface workflow-table-wrap">
        <table className="workflow-table" data-testid="payment-table">
          <thead><tr><th>NOMOR</th><th>TANGGAL</th><th>{cfg.contactLabel.toUpperCase()}</th><th>AKUN</th><th>METODE</th><th className="right">JUMLAH</th><th>AKSI</th></tr></thead>
          <tbody>
            {pager.pageItems.map((row) => (
              <tr key={row.id} onClick={() => setSelected(row)} data-testid={`payment-row-${row.id}`}>
                <td className="primary-cell">{row.number}</td>
                <td>{row.date}</td>
                <td>{row.contact}</td>
                <td>{row.account_name}</td>
                <td>{row.method}</td>
                <td className="right mono">{money(row.total)}</td>
                <td>
                  <span className="row-actions">
                    <button data-testid={`payment-open-${row.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setSelected(row); }}>Buka</button>
                    <button data-testid={`payment-edit-${row.id}`} className="table-action" onClick={(e) => { e.stopPropagation(); setEditing(row); setSelected(null); }}>Ubah</button>
                    <button data-testid={`payment-delete-${row.id}`} className="table-action danger" onClick={(e) => { e.stopPropagation(); setDeleting(row); }}>Hapus</button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="empty-state" data-testid="payment-empty-state"><CircleDollarSign size={25} /><strong>Belum ada pembayaran</strong><span>Catat pembayaran pertama Anda untuk mulai bekerja.</span></div>}
        <Pagination pager={pager} testid="payment-pagination" />
      </div>
      {formOpen && (
        <div className="modal-overlay" data-testid="payment-form-modal" onClick={() => setFormOpen(false)}>
          <div className="modal-panel wide" onClick={(e) => e.stopPropagation()}>
            <PaymentForm mode={mode} cfg={cfg} onCancel={() => setFormOpen(false)} onSaved={onSaved} onError={(text) => setMessage({ text, type: "error" })} />
          </div>
        </div>
      )}
      {editing && <PaymentEditModal cfg={cfg} payment={editing} onClose={() => setEditing(null)} onDone={onEdited} onError={(text) => { setEditing(null); setMessage({ text, type: "error" }); }} />}
      {deleting && (
        <div className="modal-overlay" data-testid="payment-delete-modal" onClick={() => setDeleting(null)}>
          <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
            <div className="form-panel">
              <div className="section-head">
                <div><p className="eyebrow">KONFIRMASI HAPUS</p><h2>Hapus Pembayaran?</h2></div>
                <button data-testid="payment-delete-close" className="icon-btn" onClick={() => setDeleting(null)}><X size={18} /></button>
              </div>
              <p className="muted delete-warning">{deleting.number} sebesar {money(deleting.total)} akan dihapus. Jurnal kas akan dibatalkan (void) dan status {cfg.docLabel} yang dilunasi dikembalikan otomatis.</p>
              <div className="form-actions">
                <button data-testid="payment-delete-confirm" className="danger-btn" onClick={remove}>Ya, Hapus</button>
                <button data-testid="payment-delete-cancel" className="secondary-btn" onClick={() => setDeleting(null)}>Batal</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {selected && <PaymentDrawer cfg={cfg} payment={selected} onClose={() => setSelected(null)} onEdit={() => { setEditing(selected); setSelected(null); }} onDelete={() => setDeleting(selected)} />}
    </div>
  );
}

export function PaymentForm({ mode, cfg, lockedAccount, onCancel, onSaved, onError }) {
  const panelRef = useRef(null);
  const [accounts, setAccounts] = useState([]);
  const [head, setHead] = useState({ contact: "", date: today(), account_code: lockedAccount?.code || "", method: "Transfer Bank", reference: "", memo: "" });
  const [invoices, setInvoices] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, []);
  useEffect(() => {
    api("/cash-accounts").then(setAccounts).catch(() => {});
  }, [cfg.contactType]);
  useEffect(() => {
    setInvoices([]); setAmounts({});
    if (head.contact) api(`/outstanding?type=${mode}&contact=${encodeURIComponent(head.contact)}`).then(setInvoices).catch(() => {});
  }, [head.contact, mode]);
  const set = (k, v) => setHead({ ...head, [k]: v });
  const setAmount = (id, v) => setAmounts({ ...amounts, [id]: v });
  const payFull = (inv) => setAmounts({ ...amounts, [inv.id]: String(inv.outstanding) });
  const total = invoices.reduce((s, inv) => s + (Number(amounts[inv.id]) || 0), 0);
  const overpaid = invoices.some((inv) => (Number(amounts[inv.id]) || 0) > inv.outstanding + 0.01);
  const valid = head.contact && head.account_code && head.date && total > 0 && !overpaid;
  const submit = async () => {
    setSaving(true);
    try {
      const allocations = invoices.filter((inv) => Number(amounts[inv.id]) > 0).map((inv) => ({ document_id: inv.id, amount: Number(amounts[inv.id]) }));
      onSaved(await api("/payments", { method: "POST", body: JSON.stringify({ type: mode, ...head, allocations }) }));
    } catch (e) { onError(e.message); }
    setSaving(false);
  };
  return (
    <div className="form-panel surface payment-form" data-testid="payment-form" ref={panelRef}>
      <div className="section-head">
        <div><p className="eyebrow">FORMULIR PEMBAYARAN</p><h2>{cfg.action}</h2></div>
        <button data-testid="payment-cancel-button" className="icon-btn" onClick={onCancel}><X size={18} /></button>
      </div>
      <div className="form-grid payment-head-grid">
        <label>{cfg.contactLabel}
          <ContactPicker
            value={head.contact}
            onPick={(c) => setHead((prev) => ({ ...prev, contact: c.name }))}
            typeFilter={cfg.contactType}
            addLabel={`${cfg.contactLabel} baru`}
            defaultTypes={[cfg.contactType]}
            testPrefix="payment-contact"
            placeholder={`Pilih ${cfg.contactLabel.toLowerCase()}...`} />
        </label>
        <label>Tanggal pembayaran<input data-testid="payment-date-input" type="date" value={head.date} onChange={(e) => set("date", e.target.value)} /></label>
        <label>{cfg.accountLabel}
          <SearchSelect testid="payment-account-select" value={head.account_code} disabled={!!lockedAccount}
            onChange={(v) => set("account_code", v)} placeholder="Pilih akun kas/bank..."
            options={(lockedAccount ? [lockedAccount] : accounts).map((a) => ({ value: a.code, label: `${a.code} · ${a.name}`, sub: money(a.balance) }))} />
        </label>
        <label>Metode pembayaran
          <select data-testid="payment-method-select" value={head.method} onChange={(e) => set("method", e.target.value)}>
            {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>No. referensi<input data-testid="payment-reference-input" value={head.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Opsional" /></label>
        <label>Memo<input data-testid="payment-memo-input" value={head.memo} onChange={(e) => set("memo", e.target.value)} placeholder="Opsional" /></label>
      </div>
      {head.contact && (
        <table className="journal-line-table" data-testid="payment-allocation-table">
          <thead><tr><th>NOMOR</th><th>TANGGAL</th><th>JATUH TEMPO</th><th className="right">TOTAL</th><th className="right">SISA TAGIHAN</th><th className="right">JUMLAH DIBAYAR</th><th /></tr></thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id} data-testid={`payment-alloc-row-${inv.number}`}>
                <td><strong>{inv.number}</strong></td>
                <td>{inv.date}</td>
                <td>{inv.due_date || "—"}</td>
                <td className="right mono">{money(inv.total)}</td>
                <td className="right mono">{money(inv.outstanding)}</td>
                <td><input data-testid={`payment-alloc-amount-${inv.number}`} className="right" type="number" min="0" max={inv.outstanding} value={amounts[inv.id] || ""} onChange={(e) => setAmount(inv.id, e.target.value)} placeholder="0" /></td>
                <td><button data-testid={`payment-pay-full-${inv.number}`} className="table-action" onClick={() => payFull(inv)}>Bayar penuh</button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={5}><strong>Total pembayaran</strong></td><td className="right mono" data-testid="payment-total">{money(total)}</td><td /></tr>
          </tfoot>
        </table>
      )}
      {head.contact && invoices.length === 0 && <div className="empty-state" data-testid="payment-no-outstanding"><strong>Tidak ada {cfg.docLabel} terbuka</strong><span>Semua {cfg.docLabel} {head.contact} sudah lunas.</span></div>}
      {overpaid && <div className="journal-balance bad" data-testid="payment-overpaid-warning">Jumlah dibayar tidak boleh melebihi sisa tagihan</div>}
      <div className="form-actions">
        <button data-testid="payment-save-button" className="primary-btn" disabled={!valid || saving} onClick={submit}>{saving ? "Menyimpan..." : `Catat ${money(total)}`}</button>
        <button data-testid="payment-form-cancel-button" className="secondary-btn" onClick={onCancel}>Batal</button>
      </div>
    </div>
  );
}

function PaymentEditModal({ cfg, payment, onClose, onDone, onError }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ date: payment.date, account_code: payment.account_code, method: payment.method, reference: payment.reference || "", memo: payment.memo || "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api("/cash-accounts").then(setAccounts).catch(() => {}); }, []);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = async () => {
    setSaving(true);
    try { onDone(await api(`/payments/${payment.id}`, { method: "PATCH", body: JSON.stringify(form) })); }
    catch (e) { onError(e.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="payment-edit-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head">
            <div><p className="eyebrow">UBAH PEMBAYARAN</p><h2>{payment.number}</h2></div>
            <button data-testid="payment-edit-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          <p className="muted delete-warning">Jumlah dan alokasi tidak dapat diubah — hapus lalu catat ulang jika perlu (seperti Mekari).</p>
          <div className="form-grid payment-head-grid">
            <label>Tanggal pembayaran<input data-testid="payment-edit-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></label>
            <label>Akun kas/bank
              <SearchSelect testid="payment-edit-account" value={form.account_code} onChange={(v) => set("account_code", v)}
                placeholder="Pilih akun kas/bank..."
                options={accounts.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}` }))} />
            </label>
            <label>Metode pembayaran
              <select data-testid="payment-edit-method" value={form.method} onChange={(e) => set("method", e.target.value)}>
                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label>No. referensi<input data-testid="payment-edit-reference" value={form.reference} onChange={(e) => set("reference", e.target.value)} /></label>
            <label>Memo<input data-testid="payment-edit-memo" value={form.memo} onChange={(e) => set("memo", e.target.value)} /></label>
          </div>
          <div className="form-actions">
            <button data-testid="payment-edit-save" className="primary-btn" disabled={saving} onClick={submit}>{saving ? "Menyimpan..." : "Simpan perubahan"}</button>
            <button data-testid="payment-edit-cancel" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentDrawer({ cfg, payment, onClose, onEdit, onDelete }) {
  const [journal, setJournal] = useState(null);
  useEffect(() => { api(`/journals/${payment.journal_id}`).then(setJournal).catch(() => {}); }, [payment.journal_id]);
  const partial = (payment.allocations || []).some((a) => a.status_after === "PARTIAL");
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="detail-drawer" data-testid="payment-detail-drawer">
        <div className="drawer-head">
          <div><p className="eyebrow">DETAIL PEMBAYARAN</p><h2>{payment.number}</h2></div>
          <button data-testid="payment-close-detail" className="icon-btn" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="detail-status"><span className={`status ${partial ? "partial" : "paid"}`} data-testid="payment-status-chip">{partial ? "DIBAYAR SEBAGIAN" : "LUNAS"}</span><span className="muted">{payment.date}</span></div>
        <div className="detail-fields">
          <div><small>{cfg.contactLabel}</small><strong>{payment.contact}</strong></div>
          <div><small>akun</small><strong>{payment.account_code} · {payment.account_name}</strong></div>
          <div><small>metode</small><strong>{payment.method}</strong></div>
          <div><small>referensi</small><strong>{payment.reference || "—"}</strong></div>
          <div><small>total</small><strong>{money(payment.total)}</strong></div>
          <div><small>jurnal</small><strong>{payment.journal_number}</strong></div>
          {payment.memo && <div><small>memo</small><strong>{payment.memo}</strong></div>}
        </div>
        <div className="journal-lines">
          <h3>Alokasi pembayaran</h3>
          {(payment.allocations || []).map((a, i) => <div key={i} data-testid={`payment-allocation-${a.document_number}`}><span>{a.document_number} <span className={`status ${a.status_after.toLowerCase()}`}>{a.status_after}</span></span><b>{money(a.amount)}</b></div>)}
        </div>
        <div className="journal-lines">
          <h3>Jurnal kas otomatis {journal ? `· ${journal.number}` : ""}</h3>
          {(journal?.lines || []).map((l, i) => <div key={i}><span>{l.account_code} · {l.description}</span><b>{l.debit ? `D ${money(l.debit)}` : `K ${money(l.credit)}`}</b></div>)}
        </div>
        <div className="drawer-actions">
          <button data-testid="payment-drawer-edit" className="secondary-btn" onClick={onEdit}>Ubah</button>
          <button data-testid="payment-drawer-delete" className="danger-btn" onClick={onDelete}>Hapus</button>
        </div>
      </div>
    </>
  );
}
